import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { buildListExcelExportHeader } from './listExcelExportHeader';

export const DEFAULT_NUMERIC_FIELDS = new Set([
    'dc_qty',
    'grn_qty',
    'rate',
    'amount',
    'total_qty',
    'accepted_qty',
    'gross_weight',
    'tare_weight',
    'total_km',
    'net_weight',
    'distance',
    'credit_limit',
    'total_outstanding',
    'invoice_amount',
    'receipt_amount',
    'balance',
    'qty',
    'load_count',
    'loadCount',
    'production_qty',
    'purchase_qty',
    'received_qty',
    'other_qty',
    'invoice_qty',
    'savings_company',
    'savings_client',
]);

const GROUP_HEADER_FILL = 'FFDBEAFE';
const GROUP_HEADER_FONT = 'FF1E3A8A';
const TOTAL_ROW_FILL = 'FFEEF2FF';
const HEADER_FILL = 'FF6E8FEC';

function sanitizeFilename(filename = 'report') {
    return String(filename).replace(/\.(xlsx|pdf|png|csv)$/i, '');
}

export function getExportableColumns(columns = []) {
    return columns.filter((col) => col.headerName !== 'ROW_ACTIONS');
}

export function parseExcelNumber(value) {
    if (value === '' || value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const cleaned = String(value).replace(/,/g, '').trim();
    if (!cleaned) {
        return null;
    }
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
}

function normalizeText(value) {
    if (value === null || value === undefined || value === 'undefined' || value === 'null') {
        return '';
    }
    return value;
}

function isNumericField(fieldName, numericFields) {
    return numericFields.has(fieldName);
}

function getCellValue(record, fieldName, numericFields) {
    const raw = record[fieldName];
    if (isNumericField(fieldName, numericFields)) {
        return parseExcelNumber(raw);
    }
    return normalizeText(raw);
}

function applyHeaderBlock(worksheet, workbook, header, columnCount) {
    if (!header) {
        return 1;
    }
    const mergeEnd = Math.max(columnCount, 4);
    let rowIndex = 1;
    let logoRowsUsed = 0;

    if (header?.logoDataUrl) {
        try {
            const base64 = header.logoDataUrl.split(',')[1];
            const extension = header.logoDataUrl.includes('image/png') ? 'png' : 'jpeg';
            const imageId = workbook.addImage({ base64, extension });
            worksheet.addImage(imageId, {
                tl: { col: 0, row: 0 },
                ext: { width: 72, height: 72 },
            });
            logoRowsUsed = 4;
        } catch (error) {
            // Skip logo when embedding fails.
        }
    }

    const textStartCol = logoRowsUsed ? 2 : 1;

    const writeMergedLine = (text, options = {}) => {
        if (!text) {
            return;
        }
        const cell = worksheet.getCell(rowIndex, textStartCol);
        cell.value = text;
        cell.font = {
            bold: options.bold !== false,
            size: options.size || 11,
            color: options.color ? { argb: options.color } : undefined,
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        worksheet.mergeCells(rowIndex, textStartCol, rowIndex, mergeEnd);
        rowIndex += 1;
    };

    if (logoRowsUsed && rowIndex < logoRowsUsed) {
        rowIndex = logoRowsUsed;
    }

    writeMergedLine(header?.companyName || '', { size: 14, color: 'FF054D9E' });
    (header?.addressLines || []).forEach((line) => {
        writeMergedLine(line, { bold: false, size: 10 });
    });
    if (header?.gstNo) {
        writeMergedLine(`GST No.: ${header.gstNo}`, { size: 10 });
    }
    if (header?.documentTitle) {
        writeMergedLine(header.documentTitle, { size: 12 });
    }
    (header?.infoLines || []).filter(Boolean).forEach((line) => {
        writeMergedLine(line, { bold: false, size: 10 });
    });

    rowIndex += 1;
    return rowIndex;
}

function styleHeaderRow(row) {
    row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: HEADER_FILL },
        };
        cell.alignment = { vertical: 'middle', wrapText: true };
    });
}

function styleGroupHeaderRow(row) {
    row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: GROUP_HEADER_FONT } };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: GROUP_HEADER_FILL },
        };
    });
}

function styleTotalRow(row) {
    row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: TOTAL_ROW_FILL },
        };
    });
}

function applyNumericFormat(cell, decimals = 2) {
    if (cell.value === null || cell.value === undefined || cell.value === '') {
        return;
    }
    if (typeof cell.value === 'number') {
        cell.numFmt = decimals === 3 ? '0.000' : '0.00';
    }
}

export async function exportReportAsExcel({
    columns,
    records,
    filename = 'report',
    header = null,
    totalsRow = null,
    numericFields = DEFAULT_NUMERIC_FIELDS,
    sheetName = 'Report',
    blankFirstRow = false,
}) {
    const exportColumns = getExportableColumns(columns);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = exportColumns.map((col) => ({
        key: col.fieldName,
        width: Math.max(12, Math.min(36, String(col.headerName || '').length + 4)),
    }));

    let headerStartRow = applyHeaderBlock(worksheet, workbook, header, exportColumns.length);
    if (blankFirstRow && !header) {
        headerStartRow = 2;
    }

    const headerRow = worksheet.getRow(headerStartRow);
    exportColumns.forEach((col, index) => {
        headerRow.getCell(index + 1).value = col.headerName;
    });
    styleHeaderRow(headerRow);

    let dataRowIndex = headerStartRow + 1;
    (records || []).forEach((record) => {
        const row = worksheet.getRow(dataRowIndex);
        const isGroupHeader = !!record.is_group_header;
        const isTotal = !!record.is_total;

        exportColumns.forEach((col, colIndex) => {
            const cell = row.getCell(colIndex + 1);
            let value = getCellValue(record, col.fieldName, numericFields);

            if (isGroupHeader && isNumericField(col.fieldName, numericFields) && value === null) {
                value = parseExcelNumber(record[col.fieldName]);
            }

            cell.value = value === null ? '' : value;

            if (col.align === 'right' || isNumericField(col.fieldName, numericFields)) {
                cell.alignment = { horizontal: 'right' };
            }

            if (isNumericField(col.fieldName, numericFields)) {
                applyNumericFormat(cell, col.fieldName === 'qty' ? 3 : 2);
            }
        });

        if (isGroupHeader) {
            styleGroupHeaderRow(row);
        }

        dataRowIndex += 1;
    });

    if (totalsRow) {
        const totalRowObj = worksheet.getRow(dataRowIndex);
        exportColumns.forEach((col, colIndex) => {
            const cell = totalRowObj.getCell(colIndex + 1);
            if (colIndex === 0 && (totalsRow[col.fieldName] === undefined || totalsRow[col.fieldName] === '')) {
                cell.value = 'TOTAL';
            } else {
                const value = getCellValue(totalsRow, col.fieldName, numericFields);
                cell.value = value === null ? '' : value;
            }
            if (isNumericField(col.fieldName, numericFields)) {
                cell.alignment = { horizontal: 'right' };
                applyNumericFormat(cell, col.fieldName === 'qty' ? 3 : 2);
            }
        });
        styleTotalRow(totalRowObj);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const safeName = sanitizeFilename(filename);
    saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `${safeName}.xlsx`,
    );
}

export async function exportReportWithPlantHeader({
    cookies,
    plantIds = [],
    columns,
    records,
    filename = 'report',
    documentTitle = 'Report',
    infoLines = [],
    totalsRow = null,
    numericFields = DEFAULT_NUMERIC_FIELDS,
    sheetName = 'Report',
}) {
    const header = await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle,
        extraInfoLines: infoLines,
    });
    await exportReportAsExcel({
        columns,
        records,
        filename,
        header,
        totalsRow,
        numericFields,
        sheetName,
    });
}

function csvEscape(value) {
    const text = value === null || value === undefined ? '' : String(value);
    if (/[",\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

export async function exportReportAsCsvWithPlantHeader({
    cookies,
    plantIds = [],
    columns,
    records,
    filename = 'report',
    documentTitle = 'Report',
    infoLines = [],
    totalsRow = null,
    numericFields = DEFAULT_NUMERIC_FIELDS,
    includeLetterhead = true,
    blankFirstRow = false,
}) {
    const header = includeLetterhead
        ? await buildListExcelExportHeader(cookies, plantIds, {
            documentTitle,
            extraInfoLines: infoLines,
        })
        : null;
    const exportColumns = getExportableColumns(columns);
    const lines = [];
    const pushRow = (cells) => {
        lines.push(cells.map(csvEscape).join(','));
    };

    if (includeLetterhead && header) {
        if (header.companyName) {
            pushRow([header.companyName]);
        }
        (header.addressLines || []).forEach((line) => pushRow([line]));
        if (header.gstNo) {
            pushRow([`GST No.: ${header.gstNo}`]);
        }
        if (header.documentTitle) {
            pushRow([header.documentTitle]);
        }
        (header.infoLines || []).filter(Boolean).forEach((line) => pushRow([line]));
        pushRow([]);
    }
    if (blankFirstRow) {
        pushRow([]);
    }
    pushRow(exportColumns.map((col) => col.headerName || ''));

    (records || []).forEach((record) => {
        pushRow(exportColumns.map((col) => {
            const value = getCellValue(record, col.fieldName, numericFields);
            return value === null || value === undefined ? '' : value;
        }));
    });

    if (totalsRow) {
        pushRow(exportColumns.map((col, colIndex) => {
            if (colIndex === 0 && (totalsRow[col.fieldName] === undefined || totalsRow[col.fieldName] === '')) {
                return 'TOTAL';
            }
            const value = getCellValue(totalsRow, col.fieldName, numericFields);
            return value === null || value === undefined ? '' : value;
        }));
    }

    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${sanitizeFilename(filename)}.csv`);
}

export function buildTotalsRow(columns, totalsByField, label = 'TOTAL') {
    const exportColumns = getExportableColumns(columns);
    const row = { is_total: true };
    exportColumns.forEach((col, index) => {
        if (index === 0) {
            row[col.fieldName] = label;
        } else if (Object.prototype.hasOwnProperty.call(totalsByField, col.fieldName)) {
            row[col.fieldName] = totalsByField[col.fieldName];
        } else {
            row[col.fieldName] = '';
        }
    });
    return row;
}
